// @ts-check
import SearchInput from 'components/SearchInput';
import debounce from 'lodash/debounce';
import { useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Container, ListContainer, StyledOption } from './ChannelSelect.style';

/**
 * @typedef {object} Channel
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {object} ChannelSelectProps
 * @property {Channel[]} channels
 * @property {Channel} [value]
 * @property {(channel: Channel) => void} onChange
 */

/**
 * @typedef {object} OptionData
 * @property {Channel[]} channelList
 * @property {Channel} [selected]
 * @property {(channel: Channel) => void} onSelect
 */

/**
 * @typedef {object} OptionProps
 * @property {OptionData} data
 * @property {React.CSSProperties} style
 * @property {number} index
 */

/**
 * @param {OptionProps} props
 * @returns {JSX.Element}
 */
const Option = ({ style, data, index }) => {
  const { selected, channelList, onSelect } = data;
  const channel = channelList?.[index];

  if (!channel) {
    return null;
  }

  return (
    <StyledOption
      style={style}
      role='option'
      onClick={() => onSelect(channel)}
      selected={selected?.id === channel.id}
    >
      <span>{`#${channel.name}`}</span>
    </StyledOption>
  );
};

/**
 *
 * @param {ChannelSelectProps} props
 * @returns {JSX.Element}
 */
export const ChannelSelect = props => {
  const { channels, onChange, value } = props;
  const [search, setSearch] = useState('');

  const listData = useMemo(() => {
    const sortedChannels = [...channels].sort((a, b) =>
      a?.name?.localeCompare(b?.name)
    );

    return {
      channelList: sortedChannels.filter(channel =>
        channel?.name?.includes(search)
      ),
      selected: value,
      onSelect: onChange
    };
  }, [channels, value, onChange, search]);

  const handleSearch = debounce(
    /**
     * @param {string} newSearch
     */
    newSearch => {
      setSearch(newSearch);
    },
    80
  );

  return (
    <Container>
      <SearchInput
        height='3.5rem'
        width='96%'
        onChange={handleSearch}
        value={search}
        placeholder={'Search by channel name'}
      />

      {listData.channelList.length > 0 && (
        <ListContainer>
          <List
            height={320}
            itemData={listData}
            itemCount={listData.channelList.length}
            itemSize={40}
          >
            {Option}
          </List>
        </ListContainer>
      )}
    </Container>
  );
};
